import React, { useEffect, useMemo, useState } from "react";
import { api, API, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, FileSpreadsheet, Download, Upload } from "lucide-react";
import { SKUMasterCombobox } from "@/components/SKUMasterCombobox";

const EMPTY = { sku: "", name: "", category: "", current_qty: 0, unit: "CASE", warehouse: "", remarks: "" };

function BulkUploadDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setFile(null); setPreview(null); } }, [open]);

  async function downloadTemplate() {
    const token = localStorage.getItem("oms_token");
    const res = await fetch(`${API}/fg-stock/template.xlsx`, { headers: { Authorization: `Bearer ${token}` } });
    const b = await res.blob(); const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = "fg_stock_template.xlsx"; a.click();
  }
  async function doPreview() {
    if (!file) return toast.error("Upload an xlsx file");
    const fd = new FormData(); fd.append("file", file);
    try {
      const token = localStorage.getItem("oms_token");
      const res = await fetch(`${API}/fg-stock/bulk-preview`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json(); if (!res.ok) throw new Error(d.detail || "Failed"); setPreview(d);
    } catch (e) { toast.error(e.message); }
  }
  async function doApply() {
    if (!file) return; setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const token = localStorage.getItem("oms_token");
      const res = await fetch(`${API}/fg-stock/bulk-apply`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json(); if (!res.ok) throw new Error(d.detail || "Failed");
      toast.success(`Created ${d.created}, updated ${d.updated}`); onOpenChange(false); onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="heading">Bulk Upload · FG Stock</DialogTitle><DialogDescription className="text-sm text-slate-500">Upsert stock rows by SKU. Existing SKUs are updated; new ones are created.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-3 gap-3 items-end">
          <div className="col-span-2"><Label>XLSX File</Label><Input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0])} data-testid="fg-bulk-file" /></div>
          <Button variant="outline" onClick={downloadTemplate} data-testid="fg-template"><Download className="h-4 w-4 mr-1" /> Template</Button>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={doPreview}><FileSpreadsheet className="h-4 w-4 mr-1" /> Preview</Button>
          <Button onClick={doApply} disabled={busy || !file} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="fg-bulk-apply"><Upload className="h-4 w-4 mr-1" /> {busy ? "Uploading…" : "Apply"}</Button>
        </div>
        {preview && (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="p-3 bg-slate-50 border-b text-sm">{preview.count} rows · <b>{preview.items.filter((i) => i.action === "create").length}</b> new · <b>{preview.items.filter((i) => i.action === "update").length}</b> updates</div>
            <div className="max-h-64 overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 sticky top-0"><tr><th className="p-2 text-left">Action</th><th className="p-2 text-left">SKU</th><th className="p-2 text-left">Name</th><th className="p-2 text-right">Qty</th><th className="p-2 text-left">Warehouse</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.items.map((it, i) => (<tr key={i}><td className="p-2"><span className={`text-[10px] px-2 py-0.5 rounded border ${it.action === "create" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>{it.action}</span></td><td className="p-2 mono">{it.sku}</td><td className="p-2">{it.name}</td><td className="p-2 text-right">{it.current_qty}</td><td className="p-2 text-xs text-slate-600">{it.warehouse}</td></tr>))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function FGStock() {
  const { user } = useAuth();
  const canEdit = ["super_admin", "admin", "production", "operations"].includes(user?.role);
  const canDelete = ["super_admin", "admin", "operations"].includes(user?.role);
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [selected, setSelected] = useState(new Set());

  async function load() { const { data } = await api.get("/fg-stock"); setList(data); setSelected(new Set()); }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => list.filter((s) => !search || (s.sku + " " + (s.name || "") + " " + (s.category || "")).toLowerCase().includes(search.toLowerCase())), [list, search]);

  function toggle(id) { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); }
  function toggleAll() { setSelected(selected.size === filtered.length ? new Set() : new Set(filtered.map((r) => r.id))); }

  async function bulkDelete() {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} stock entr${selected.size > 1 ? "ies" : "y"}?`)) return;
    try { const { data } = await api.post("/fg-stock/bulk-delete", { ids: Array.from(selected) }); toast.success(`${data.deleted} deleted`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  function startNew() { setForm(EMPTY); setEditId(null); setOpen(true); }
  function startEdit(s) { setForm({ ...s }); setEditId(s.id); setOpen(true); }

  async function save() {
    try {
      const payload = { ...form, current_qty: Number(form.current_qty || 0) };
      if (editId) await api.patch(`/fg-stock/${editId}`, payload);
      else await api.post("/fg-stock", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function remove(s) {
    if (!window.confirm(`Delete stock entry for ${s.sku}?`)) return;
    try { await api.delete(`/fg-stock/${s.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-[28px] font-bold text-slate-900 tracking-tight">Finished Goods Stock</h1>
          <p className="text-sm text-slate-500 mt-1">Factory on-hand stock per SKU. Used by the Production Sheet to calculate net production required.</p>
        </div>
        {canEdit && <div className="flex gap-2">
          {canDelete && selected.size > 0 && <Button onClick={bulkDelete} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="fg-bulk-delete"><Trash2 className="h-4 w-4 mr-1.5" /> Delete {selected.size}</Button>}
          <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="fg-bulk-btn"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Bulk Upload</Button>
          <Button onClick={startNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="new-fg-btn"><Plus className="h-4 w-4 mr-1.5" /> New Stock Entry</Button>
        </div>}
      </div>

      <div className="relative max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search SKU / name / category…" className="pl-9" data-testid="fg-search" />
      </div>

      <Card className="border border-slate-200 shadow-none overflow-hidden rounded-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wide">
              <tr>
                {canDelete && <th className="p-3.5 w-8"><Checkbox checked={filtered.length > 0 && selected.size === filtered.length} onCheckedChange={toggleAll} data-testid="fg-select-all" /></th>}
                <th className="p-3.5 text-left">SKU</th>
                <th className="p-3.5 text-left">Name</th>
                <th className="p-3.5 text-left">Category</th>
                <th className="p-3.5 text-right">Current Qty</th>
                <th className="p-3.5 text-left">Unit</th>
                <th className="p-3.5 text-left">Warehouse</th>
                <th className="p-3.5 text-left">Last Updated</th>
                <th className="p-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 ? <tr><td colSpan={canDelete ? 9 : 8} className="p-12 text-center text-slate-500">No stock entries yet.</td></tr> :
                filtered.map((s) => (
                  <tr key={s.id} className="table-row-hover">
                    {canDelete && <td className="p-3.5"><Checkbox checked={selected.has(s.id)} onCheckedChange={() => toggle(s.id)} data-testid={`fg-sel-${s.sku}`} /></td>}
                    <td className="p-3.5 mono text-[12px] font-semibold text-slate-900">{s.sku}</td>
                    <td className="p-3.5 text-slate-900">{s.name || "—"}</td>
                    <td className="p-3.5 text-slate-600 text-xs">{s.category || "—"}</td>
                    <td className="p-3.5 text-right font-medium">{Number(s.current_qty || 0).toLocaleString()}</td>
                    <td className="p-3.5 text-slate-600 text-xs">{s.unit || "CASE"}</td>
                    <td className="p-3.5 text-slate-600 text-xs">{s.warehouse || "—"}</td>
                    <td className="p-3.5 text-slate-500 text-xs">{fmtDateTime(s.updated_at)}</td>
                    <td className="p-3.5 text-right">
                      <div className="flex gap-1 justify-end">
                        {canEdit && <Button variant="ghost" size="icon" onClick={() => startEdit(s)} data-testid={`fg-edit-${s.sku}`}><Pencil className="h-4 w-4" /></Button>}
                        {canDelete && <Button variant="ghost" size="icon" onClick={() => remove(s)} data-testid={`fg-del-${s.sku}`} className="text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle className="heading">{editId ? "Edit" : "New"} Stock Entry</DialogTitle><DialogDescription className="text-xs text-slate-500">Factory on-hand stock per SKU. SKUs are sourced from SKU Master — add new SKUs there first if needed. Existing legacy SKUs remain read-only.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>SKU</Label>
              <SKUMasterCombobox
                value={form.sku}
                onChange={(sku) => setForm({ ...form, sku })}
                disabled={!!editId}
                placeholder="Pick from SKU Master…"
                testId="fg-sku-combo"
              />
            </div>
            <div><Label>Current Qty</Label><Input type="number" min="0" value={form.current_qty} onChange={(e) => setForm({ ...form, current_qty: e.target.value })} data-testid="fg-qty" /></div>
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
            <div><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="CASE" /></div>
            <div className="col-span-2"><Label>Warehouse / Location</Label><Input value={form.warehouse} onChange={(e) => setForm({ ...form, warehouse: e.target.value })} placeholder="Hyderabad · FG-01" /></div>
            <div className="col-span-2"><Label>Remarks</Label><Textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="fg-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <BulkUploadDialog open={bulkOpen} onOpenChange={setBulkOpen} onDone={load} />
    </div>
  );
}
